import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useEntitlement } from "./useEntitlement";

/**
 * Renders {@code children} when the current tenant holds the entitlement,
 * the Pro upgrade CTA otherwise. Wires to the BE via the {@code useEntitlement}
 * hook (T66 endpoint /api/v1/me/entitlements).
 *
 * Backend still enforces every Pro endpoint with @RequiresEntitlement — this
 * gate is for UX clarity, not security.
 */
export interface EntitlementGateProps {
  code: string;
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * Rendered while the entitlement check is in flight. Pages that already
   * show their own skeleton can pass {@code null} to render nothing during
   * the brief gate-check window.
   */
  loadingPlaceholder?: ReactNode;
}

export function EntitlementGate({
  code,
  children,
  fallback,
  loadingPlaceholder,
}: EntitlementGateProps) {
  const { enabled, isLoading } = useEntitlement(code);

  if (isLoading) {
    return <>{loadingPlaceholder ?? null}</>;
  }
  if (enabled) {
    return <>{children}</>;
  }
  return (
    <>
      {fallback ?? (
        <div className="flex flex-col items-center rounded-lg border border-dashed border-border px-6 py-12 text-center">
          <h2 className="text-lg font-semibold text-foreground">Tính năng này thuộc gói Pro</h2>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            Yêu cầu entitlement:{" "}
            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{code}</span>. Nâng
            cấp gói để mở khoá.
          </p>
          <Link
            to="/settings/subscription"
            className="mt-4 inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Xem gói nâng cấp
          </Link>
        </div>
      )}
    </>
  );
}
